/**
 * Müllabfuhr: kleiner ICS-Parser (RFC 5545, nur was der Abfuhrkalender
 * Hildesheim liefert), Normalisierung auf Tonnen-Typen, Hinweis-Logik
 * („heute Abend rausstellen") und Merge-Logik für den Cache.
 */
import { addDays, berlinDate, berlinParts, weekday } from './time.js';

export interface IcsEvent {
  uid: string;
  /** Rohwert DTSTART */
  dtstart: string;
  /** Abfuhrtag, lokal Europe/Berlin, 'YYYY-MM-DD' */
  date: string;
  summary: string;
  description: string;
}

/** Entfernt ICS-Escapes: \, \; \n \\ */
export function unescapeIcsText(v: string): string {
  return v.replace(/\\([\\,;nN])/g, (_, c: string) => (c === 'n' || c === 'N' ? '\n' : c));
}

/** Zeilen entfalten (CRLF gefolgt von Leerzeichen/Tab = Fortsetzung). */
export function unfoldIcs(text: string): string[] {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n[ \t]/g, '').split('\n');
}

/** DTSTART → lokales Berliner Datum. Unterstützt UTC (…Z), lokale Zeit und VALUE=DATE. */
export function icsDateToLocalDate(value: string): string {
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z)?)?$/.exec(value.trim());
  if (!m) throw new Error(`Ungültiges ICS-Datum: ${value}`);
  const [, y, mo, d, h, mi, s, z] = m;
  if (h !== undefined && z) {
    return berlinDate(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s));
  }
  // Ganztägig oder lokale Zeit (TZID bzw. floating): Datum direkt übernehmen.
  return `${y}-${mo}-${d}`;
}

export function parseIcs(text: string): IcsEvent[] {
  const events: IcsEvent[] = [];
  let cur: Record<string, string> | null = null;
  for (const line of unfoldIcs(text)) {
    if (!line.trim()) continue;
    if (line === 'BEGIN:VEVENT') {
      cur = {};
      continue;
    }
    if (line === 'END:VEVENT') {
      if (cur && cur.DTSTART) {
        events.push({
          uid: cur.UID ?? '',
          dtstart: cur.DTSTART,
          date: icsDateToLocalDate(cur.DTSTART),
          summary: unescapeIcsText(cur.SUMMARY ?? '').trim(),
          description: unescapeIcsText(cur.DESCRIPTION ?? '').trim(),
        });
      }
      cur = null;
      continue;
    }
    if (!cur) continue;
    const colon = line.indexOf(':');
    if (colon < 0) continue;
    const name = line.slice(0, colon).split(';')[0].toUpperCase();
    cur[name] = line.slice(colon + 1);
  }
  return events;
}

export type WasteType = 'restmuell' | 'bio' | 'gelb' | 'papier' | 'sonstiges';

export const WASTE_TYPES: Record<WasteType, { label: string; color: string; text: string }> = {
  restmuell: { label: 'Restmüll', color: '#3a3a3a', text: '#ffffff' },
  bio: { label: 'Biotonne', color: '#7a4a1e', text: '#ffffff' },
  gelb: { label: 'Gelbe Tonne', color: '#f2c200', text: '#1a1a1a' },
  papier: { label: 'Altpapier', color: '#1f5fbf', text: '#ffffff' },
  sonstiges: { label: 'Abfuhr', color: '#6b7280', text: '#ffffff' },
};

export interface WasteInfo {
  type: WasteType;
  label: string;
  /** Termin wurde verschoben (z. B. wegen Feiertag) */
  shifted: boolean;
  /** Bereinigter Text ohne abgeschnittene Klammern */
  clean: string;
}

/**
 * Normalisiert die (teils abgeschnittenen) SUMMARY-Texte, z. B.
 * „Abfuhr Restabfall (14tägige und vierwöchentliche Abfuhr" oder
 * „… (verschoben)".
 */
export function normalizeWaste(summary: string): WasteInfo {
  const s = summary.toLowerCase();
  let type: WasteType = 'sonstiges';
  if (/rest(abfall|müll|muell)/.test(s)) type = 'restmuell';
  else if (/bio/.test(s)) type = 'bio';
  else if (/gelb|wertstoff|leichtverpack/.test(s)) type = 'gelb';
  else if (/papier|pappe/.test(s)) type = 'papier';
  const shifted = /verschoben/.test(s);
  // Alles ab der ersten Klammer abschneiden – die Klammern sind oft unvollständig.
  const clean = summary.replace(/\s*\(.*$/, '').trim();
  return { type, label: WASTE_TYPES[type].label, shifted, clean };
}

export interface WasteEvent {
  /** Eindeutiger Schlüssel: Datum + Typ (die UIDs der Quelle sind nicht stabil). */
  key: string;
  uid: string;
  date: string;
  type: WasteType;
  summary: string;
  shifted: boolean;
}

export function wasteKey(date: string, type: WasteType): string {
  return `${date}|${type}`;
}

export function toWasteEvents(ics: IcsEvent[]): WasteEvent[] {
  const byKey = new Map<string, WasteEvent>();
  for (const e of ics) {
    const n = normalizeWaste(e.summary);
    const key = wasteKey(e.date, n.type);
    byKey.set(key, { key, uid: e.uid, date: e.date, type: n.type, summary: e.summary, shifted: n.shifted });
  }
  return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key));
}

export interface MergeResult {
  upsert: WasteEvent[];
  /** Schlüssel, die entfernt werden (Termin in der Zukunft verschwunden → verlegt/abgesagt) */
  remove: string[];
}

/**
 * Merge des frischen Abrufs in den Cache. Die Quelle liefert nur Termine ab
 * heute; vergangene Termine bleiben daher erhalten. Zukünftige Termine des
 * abgerufenen Jahres, die in der Quelle fehlen, werden entfernt – aber nur,
 * wenn der Abruf plausibel ist (nicht leer). Ein leerer Abruf ändert nichts.
 */
export function mergeWasteEvents(
  existing: WasteEvent[],
  fetched: WasteEvent[],
  opts: { today: string; year: number },
): MergeResult {
  if (fetched.length === 0) return { upsert: [], remove: [] };
  const fetchedKeys = new Set(fetched.map((e) => e.key));
  const existingByKey = new Map(existing.map((e) => [e.key, e]));
  const upsert = fetched.filter((f) => {
    const old = existingByKey.get(f.key);
    // UID-Wechsel allein zählt nicht als Änderung (die Quelle nummeriert bei jedem Abruf neu).
    return !old || old.summary !== f.summary || old.shifted !== f.shifted;
  });
  const yearPrefix = `${opts.year}-`;
  const remove = existing
    .filter((e) => e.date.startsWith(yearPrefix) && e.date >= opts.today && !fetchedKeys.has(e.key))
    .map((e) => e.key);
  return { upsert, remove };
}

export interface WasteNotice {
  /** Abfuhr morgen → heute Abend rausstellen */
  tonight: WasteType[];
  /** Abfuhr heute (Hinweis morgens bis 12 Uhr) */
  today: WasteType[];
  /** Freitag/Samstag: Abfuhr am Montag → am Sonntagabend rausstellen */
  monday: WasteType[];
  tomorrowDate: string;
}

/** Ermittelt die Hinweise für die Startseite zum Zeitpunkt nowMs (Europe/Berlin). */
export function wasteNotice(events: Pick<WasteEvent, 'date' | 'type'>[], nowMs: number): WasteNotice {
  const today = berlinDate(nowMs);
  const hour = berlinParts(nowMs).h;
  const tomorrow = addDays(today, 1);
  const typesOn = (d: string) => [...new Set(events.filter((e) => e.date === d).map((e) => e.type))];
  const wd = weekday(today);
  let monday: WasteType[] = [];
  if (wd === 5) monday = typesOn(addDays(today, 3));
  else if (wd === 6) monday = typesOn(addDays(today, 2));
  return {
    tonight: typesOn(tomorrow),
    today: hour < 12 ? typesOn(today) : [],
    monday,
    tomorrowDate: tomorrow,
  };
}

/** Kommende Termine ab heute (inklusive), sortiert. */
export function upcomingWaste<T extends Pick<WasteEvent, 'date' | 'type'>>(events: T[], today: string, limit = 12): T[] {
  return events
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date) || a.type.localeCompare(b.type))
    .slice(0, limit);
}

export function joinLabels(types: WasteType[]): string {
  const labels = types.map((t) => WASTE_TYPES[t].label);
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} und ${labels[labels.length - 1]}`;
}
